// src/components/Menu/MenuKit.jsx
// ======================================================================
// تصميم موحّد لقوائم النظام (المبيعات، المشتريات، المحاسبة...) مع دعم اللغتين
//
//   <MenuPage titleKey="accountsMenu.title" titleFallback="المالية والحسابات" icon={<Icon/>}
//             sections={[{ key, titleKey, titleFallback, icon, color, items:[...] }]} />
//
// كل عنصر: { key, nameKey, nameFallback, descKey?, descFallback?, icon, color, link }
// النصوص تُترجم عبر i18next — والـ fallback العربي يظهر حتى تُضاف الترجمة للملفات
// ======================================================================
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography, ButtonBase, TextField, InputAdornment, Alert, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const BORDER = "#e5e7eb";

export const useMenuT = () => {
  const { t, i18n } = useTranslation();
  const tr = (key, fallback) => (key ? t(key, { defaultValue: fallback }) : fallback);
  const dir = i18n.dir ? i18n.dir(i18n.language) : (i18n.language === "en" ? "ltr" : "rtl");
  return { tr, dir, lang: i18n.language };
};

// ───────────────────────── البطاقة (مربع: أيقونة + اسم) ─────────────────────────
export function MenuCard({ item, onClick, size = "md" }) {
  const lg = size === "lg";
  const { tr } = useMenuT();
  const name = tr(item.nameKey, item.nameFallback);
  const desc = item.descFallback || item.descKey ? tr(item.descKey, item.descFallback) : undefined;
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      title={desc}
      aria-label={name}
      sx={{
        width: "100%", height: "100%", minHeight: lg ? { xs: 120, sm: 150, md: 164 } : { xs: 100, sm: 120, md: 128 },
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: { xs: .75, sm: 1.1 },
        p: { xs: 1, sm: 1.5 }, textAlign: "center",
        borderRadius: { xs: "16px", sm: "18px" }, bgcolor: "#fff", border: `1px solid ${BORDER}`,
        boxShadow: "0 1px 2px rgba(15,23,42,.04)",
        transition: "border-color .2s, box-shadow .2s, transform .2s",
        "&:hover, &.Mui-focusVisible": {
          borderColor: item.color,
          boxShadow: `0 14px 28px -16px ${item.color}`,
          transform: "translateY(-3px)",
          "& .mc-icon": { bgcolor: item.color, color: "#fff", transform: "scale(1.06)" },
        },
        "&:active": { transform: "translateY(-1px)" },
      }}
    >
      <Box className="mc-icon" sx={{
        width: lg ? { xs: 52, sm: 62, md: 68 } : { xs: 42, sm: 50, md: 54 }, height: lg ? { xs: 52, sm: 62, md: 68 } : { xs: 42, sm: 50, md: 54 }, flexShrink: 0,
        borderRadius: { xs: "14px", sm: "16px" },
        display: "grid", placeItems: "center", bgcolor: `${item.color}14`, color: item.color,
        transition: "all .2s", "& svg": { fontSize: lg ? { xs: 26, sm: 30, md: 33 } : { xs: 21, sm: 25, md: 27 } },
      }}>
        {item.icon}
      </Box>
      <Typography sx={{
        fontWeight: 800, fontSize: lg ? { xs: 13.5, sm: 15, md: 15.5 } : { xs: 12, sm: 13.5, md: 14 }, color: "#0f172a", lineHeight: 1.4,
        width: "100%", px: .5,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {name}
      </Typography>
    </ButtonBase>
  );
}

const GRID_COLS = {
  xs: "repeat(3, minmax(0,1fr))", sm: "repeat(4, minmax(0,1fr))", md: "repeat(5, minmax(0,1fr))",
  lg: "repeat(6, minmax(0,1fr))", xl: "repeat(7, minmax(0,1fr))",
};

// ───────────────────────── القسم ─────────────────────────
export function MenuSection({ section, items, loading }) {
  const navigate = useNavigate();
  const { tr } = useMenuT();
  if (!loading && !items.length) return null;
  const { color } = section;
  return (
    <Box component="section" sx={{ mb: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box sx={{
          width: 34, height: 34, flexShrink: 0, borderRadius: "10px", display: "grid", placeItems: "center",
          bgcolor: `${color}14`, color, "& svg": { fontSize: 19 },
        }}>{section.icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 15.5, md: 16.5 }, color: "#0f172a", lineHeight: 1.3 }}>
            {tr(section.titleKey, section.titleFallback)}
            {!loading && (
              <Typography component="span" sx={{ marginInlineStart: 1, fontSize: 12.5, fontWeight: 700, color: "#94a3b8" }}>
                ({items.length})
              </Typography>
            )}
          </Typography>
          {(section.subtitleKey || section.subtitleFallback) && (
            <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{tr(section.subtitleKey, section.subtitleFallback)}</Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER, marginInlineStart: 1 }} />
      </Box>

      {section.note}

      <Box sx={{ display: "grid", gap: { xs: 1, sm: 1.25 }, gridTemplateColumns: GRID_COLS }}>
        {loading
          ? [...Array(5)].map((_, i) => <Skeleton key={i} variant="rounded" sx={{ borderRadius: "16px", height: { xs: 100, sm: 120, md: 128 } }} />)
          : items.map(item => <MenuCard key={item.key || item.link} item={item} onClick={() => navigate(item.link)} />)}
      </Box>
    </Box>
  );
}

// ───────────────────────── الترويسة (نفس ترويسة التقارير) ─────────────────────────
export const headerSearchSx = {
  width: { xs: "100%", sm: 300 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px", color: "#fff", bgcolor: "rgba(255,255,255,.14)",
    "& fieldset": { borderColor: "rgba(255,255,255,.3)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,.6)" },
    "&.Mui-focused fieldset": { borderColor: "#fff" },
  },
  "& input::placeholder": { color: "rgba(255,255,255,.75)", opacity: 1 },
};

/** ترويسة القوائم: أيقونة + عنوان + وصف، وما تمرّره children يظهر في الطرف الآخر (بحث، تاريخ...) */
export function MenuHeader({ icon, title, subtitle, eyebrow, children }) {
  return (
    <Paper elevation={0} sx={{
      position: "relative", overflow: "hidden", mb: 2, borderRadius: "16px", color: "#fff",
      px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.75, sm: 2.25 },
      background: "linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)",
      boxShadow: "0 18px 40px -22px rgba(67,56,202,.75)",
    }}>
      <Box aria-hidden sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(420px 220px at 12% -40%, rgba(255,255,255,.22), transparent 70%), radial-gradient(360px 200px at 100% 140%, rgba(16,185,129,.35), transparent 70%)",
      }} />
      <Box sx={{ position: "relative", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
          <Box sx={{
            width: { xs: 44, sm: 50 }, height: { xs: 44, sm: 50 }, flexShrink: 0, borderRadius: "13px",
            display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.28)",
            "& svg": { fontSize: { xs: 23, sm: 26 } },
          }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {eyebrow && <Typography sx={{ opacity: .9, fontSize: { xs: 13, sm: 14 }, fontWeight: 600 }}>{eyebrow}</Typography>}
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.6rem" }, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && <Typography sx={{ opacity: .85, fontSize: { xs: 13, sm: 14 } }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {children}
      </Box>
    </Paper>
  );
}

// ───────────────────────── الصفحة ─────────────────────────
/**
 * @param sections [{ key, titleKey, titleFallback, subtitleKey?, subtitleFallback?, icon, color, items, loading?, note? }]
 * @param warning  رسالة تنبيه اختيارية أعلى الأقسام
 */
export function MenuPage({ titleKey, titleFallback, subtitleKey, subtitleFallback, icon, sections, warning }) {
  const { tr, dir } = useMenuT();
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();

  const filtered = sections.map(s => ({
    ...s,
    items: (s.items || []).filter(Boolean).filter(i => {
      if (!q) return true;
      const text = `${tr(i.nameKey, i.nameFallback)} ${i.descKey || i.descFallback ? tr(i.descKey, i.descFallback) : ""}`;
      return text.toLowerCase().includes(q);
    }),
  }));
  const nothing = q && filtered.every(s => !s.items.length);

  return (
    <Box dir={dir} sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
      <MenuHeader
        icon={icon}
        title={tr(titleKey, titleFallback)}
        subtitle={subtitleKey || subtitleFallback ? tr(subtitleKey, subtitleFallback) : null}
      >
        <TextField
          size="small" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={tr("menu.searchPlaceholder", "ابحث عن شاشة أو تقرير...")}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "rgba(255,255,255,.8)" }} /></InputAdornment> }}
          inputProps={{ "aria-label": tr("menu.search", "بحث") }}
          sx={headerSearchSx}
        />
      </MenuHeader>

      {warning && <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>{warning}</Alert>}

      {filtered.map(s => <MenuSection key={s.key} section={s} items={s.items} loading={s.loading && !q} />)}

      {nothing && (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px dashed ${BORDER}`, color: "#64748b" }}>
          <SearchIcon sx={{ fontSize: 36, color: "#cbd5e1" }} />
          <Typography sx={{ fontWeight: 700, mt: .5 }}>
            {tr("menu.noResults", "لا توجد شاشة بهذا الاسم")}: "{search}"
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
